import React, {forwardRef, useImperativeHandle, useMemo, useRef} from "react";
import {Col, Row} from "reactstrap";
import {OutlinedDropdownForm} from "../../../../../../../../components/base/dropdown/outlined_dropdown";
import Form from "../../../../../../../../components/base/form";
import * as Yup from "yup";
import ValidateMessages from "../../../../../../../../../core/models/static/texts/vallidate-messages";
import {makeRequired, makeValidate} from "mui-rff";
import {OutlinedInputForm} from "../../../../../../../../components/base/input/outlined_input";
import Paper from "@mui/material/Paper";
import Api from "../../../../../../../../../core/services/api/api";
import useIsMounted from "../../../../../../../../hooks/use-is-mounted";


const formKeys = {
    modifier: 'modifier',
    scale: 'scale',
    amount: 'amount'
}

const modifierTypes = {
    increase: 'increase',
    decrease: 'decrease',
}

const scaleTypes = {
    dollar: 'dollar',
    percentage: 'percentage',
}

const schema = Yup.object().shape({
    [formKeys.modifier]: Yup.string().nullable().required(ValidateMessages.required),
    [formKeys.scale]: Yup.string().nullable().required(ValidateMessages.required),
    [formKeys.amount]: Yup.number().min(0.01).typeError(ValidateMessages.incorrectType("number")).nullable().required(ValidateMessages.required),
});

const validate = makeValidate(schema);
const required = makeRequired(schema);

const BulkPriceChangeChangePriceSection = forwardRef(({
                                                          requestedAmount,
                                                          isPercentage,
                                                          setPriceChangeState,
                                                          requestId,
                                                          affectedPriceLevels,
                                                          totalAffectedProducts,
                                                      }, ref) => {
    const isMounted = useIsMounted();
    /**@type {React.MutableRefObject<formApi>}*/
    const formRef = useRef();

    const modifierOptions = useMemo(() => [
        {
            id: 1,
            label: 'Increase',
            value: modifierTypes.increase,
        },
        {
            id: 2,
            label: 'Decrease',
            value: modifierTypes.decrease,
        }
    ], [])

    const scaleOptions = useMemo(() => [
        {
            id: 1,
            label: '$',
            value: scaleTypes.dollar,
        },
        {
            id: 2,
            label: '%',
            value: scaleTypes.percentage,
        }
    ], [])

    const initialValues = useMemo(() => ({
            [formKeys.modifier]: requestedAmount < 0 ? modifierTypes.decrease : modifierTypes.increase,
            [formKeys.scale]: isPercentage ? scaleTypes.percentage : scaleTypes.dollar,
            [formKeys.amount]: Math.abs(requestedAmount ?? 0),
        }
    ), [requestedAmount, modifierOptions, isPercentage, scaleOptions])

    /**
     * Attaches a [submit] callback to the ref object of this component.
     */
    useImperativeHandle(ref, () => ({
        async submit() {
            return updateBulkPriceChangePriceChangeInfo();
        }
    }), [])

    /**
     * Updates the price change information of the current bulk-price-change request by submitting its respective form.
     * @return {Promise<boolean>}
     */
    const updateBulkPriceChangePriceChangeInfo = async () => {
        const validationErrors = await formRef.current.submit()
        return !validationErrors;
    }

    /**
     * Updates the price change information of the current bulk-price-change request by calling the server api.
     *
     * @param {any} values
     * @return {Promise<undefined | Record<string, string>>}
     */
    const onSubmit = async (values) => {
        const forApi = {
            requestId: requestId,
            value: Number(values[formKeys.amount]) * (values[formKeys.modifier] === modifierTypes.increase ? 1 : -1),
            isPercentage: values[formKeys.scale] === scaleTypes.percentage,
        }
        const response = await Api.updateBulkPriceChangePriceChangeInfo(forApi);
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            setPriceChangeState(
                forApi.value,
                forApi.isPercentage,
            );
        }
        return response?.resultFlag ? undefined : {};
    }

    return (
        <>
            <Paper className={'paper content change-price-section align-items-start h-75'} style={{borderRadius: '0 0 5px 5px'}}>
                <Form
                    className='w-100 mt-5'
                    onSubmit={onSubmit}
                    validate={validate}
                    initialValues={initialValues}
                    render={({form}) => {
                        formRef.current = form;
                        return (
                            <Row className='no-gutters align-items-start justify-content-center px-0 px-lg-5 w-100'>
                                <Col className='modifier'>
                                    <OutlinedDropdownForm
                                        label={'Modifier'}
                                        name={formKeys.modifier}
                                        required={!!required[formKeys.modifier]}
                                        onBlur={() => form.submit()}
                                        renderValue={(val) => modifierOptions.find(e => e.value === val)?.label}
                                        data={modifierOptions}
                                    />
                                </Col>
                                <Col className='text'>
                                    <p>
                                        {`the current price of the ${affectedPriceLevels} price level${affectedPriceLevels !== 1 ? 's' : ''} of ${totalAffectedProducts} product${totalAffectedProducts !== 1 ? 's' : ''}`}
                                    </p>
                                </Col>
                                <Col className='amount'>
                                    <OutlinedInputForm
                                        className={'transition'}
                                        label={'Amount'}
                                        type={'number'}
                                        onBlur={() => form.submit()}
                                        name={formKeys.amount}
                                        required={required[formKeys.amount]}
                                    />
                                </Col>
                                <Col className='scale'>
                                    <OutlinedDropdownForm
                                        label={'Value Type'}
                                        name={formKeys.scale}
                                        required={!!required[formKeys.scale]}
                                        onBlur={() => form.submit()}
                                        renderValue={(val) => scaleOptions.find(e => e.value === val)?.label}
                                        data={scaleOptions}
                                    />
                                </Col>
                            </Row>
                        );
                    }}
                />
            </Paper>
        </>
    );
})

export default BulkPriceChangeChangePriceSection;
